// The epoch rules of SuiteModel/DocumentStore+Epochs.swift, ported as they are (`absorb(_:into:…)`, `rebase`,
// `adoptFragmentEpoch`, `absorbOlderFragmentEpoch`), for a client of the relay, where an older history's edits arrive as
// batches: the caller builds the older-epoch document the Swift would receive whole (the relay's copy of that history:
// the archived copy as synced, plus the batch) and hands over the archived copy for the Swift's third case.
//
// Adopting a newer epoch splits this device's copy of the old history in two:
// - what the relay has (`synced`: everything received, and this device's pushes it acknowledged) is *absorbed* into the
//   new history exactly as the Swift absorbs an older epoch — which records what it took in (`absorbedHeads` in the
//   compaction record), so a second delivery of those edits, here or on a Mac, adds nothing;
// - what only this device has (typing never sent) is *rebased* ("Merged this device's edits into the compacted
//   history"), from the synced state joined with the compaction's `fromHeads`, as the Swift's `rebase`.
import * as A from '@automerge/automerge';
import { decodeHeads, decodeHeadsIn, encodeHeads } from '../automerge/heads.ts';
import { headsList, holds } from './changes.ts';
import { compactionRecordOf, describeEpoch, epochIDOf, sameEpoch, writeCompactionRecord } from './epochID.ts';
import { reanchor, remapCheckpointProjectHeads } from './reanchor.ts';
import { mergeTrees, readTree, sameTree, withEpochKeysOf, withoutEpochKeys, writeTree, type RootTree } from './tree.ts';

export const ABSORB_MESSAGE = 'Merged edits made to the version before compaction';
export const REBASE_MESSAGE = "Merged this device's edits into the compacted history";

/** `ModelError.differentEpoch`: edits of an older history that can't be placed here. */
export class DifferentEpoch extends Error {}

/** One side of an epoch change: a document and the heads its content is known to have reached the relay by. */
export interface EpochSide {
  doc: A.Doc<unknown>;
  /** Heads (hex) this device and the relay agreed on: everything received, and this device's acknowledged pushes. */
  synced: string[];
  /** Heads whose content was already rebased into a newer epoch here: never rebased again (the web's addition). */
  rebased?: string[];
}

const seconds = (d: Date) => Math.floor(d.getTime() / 1000);

function decodeOrEmpty(hex: string): string[] {
  try {
    return decodeHeads(hex);
  } catch {
    return [];
  }
}

/** A byte copy of `doc` as it stood at `heads` (the relay's copy of it, when `heads` are the synced ones). */
export function copyAt(doc: A.Doc<unknown>, heads: readonly string[]): A.Doc<unknown> {
  const now = headsList(A.getHeads(doc));
  const at = headsList(heads);
  if (now.length === at.length && now.every((h, i) => h === at[i])) return A.load(A.save(doc));
  return A.load(A.getBackend(doc).fork(undefined, at).save());
}

/** Writes `tree` (and an optional record change) into `local` as one change by its own actor; the document unchanged
 *  when nothing differs. */
function commitInto(local: A.Doc<unknown>, message: string, at: Date, write: (raw: ReturnType<typeof A.getBackend>) => void): A.Doc<unknown> {
  const raw = A.getBackend(local).fork(A.getActorId(local));
  write(raw);
  const hash = raw.commit(message, seconds(at));
  const change = hash ? raw.getLastLocalChange() : null;
  if (!change) return local;
  return A.applyChanges(local, [change])[0];
}

export interface Absorbed {
  doc: A.Doc<unknown>;
  /** Whether `doc` changed (`absorb` returns `local.heads() != before`). */
  changed: boolean;
  /** The archived copy with `remote` merged in, when the Swift's third case used it (to keep). */
  archived: A.Doc<unknown> | null;
}

/**
 * `DocumentStore.absorb(_:into:projectID:name:)`: edits made to an older epoch (`remote`) merged into `local`. The base is,
 * in order: the state `local` was compacted from (its record's `fromHeads`, with the heads already absorbed), when
 * `remote` is that epoch; the state `remote` was itself compacted at (`toHeads`, with those absorbed), when both are
 * compactions of one epoch; else `archived` (a copy of `remote`'s epoch kept here), into which `remote` is merged; else
 * `DifferentEpoch`. Theirs is `remote` (or the archive). The three-way merge leaves the epoch keys as `local` has them.
 * Unless the archive was used, the record's `absorbedHeads` becomes `remote`'s heads plus those absorbed before that
 * `remote` lacks, so a second delivery — to this device or any other that has this change — adds nothing. One commit,
 * "Merged edits made to the version before compaction".
 *
 * Accepted window, as among Apple devices: two devices absorbing the same delivery at once, each before seeing the
 * other's `absorbedHeads`, both take the edits in.
 */
export function absorbOlder(local: A.Doc<unknown>, remote: A.Doc<unknown>, archived: A.Doc<unknown> | null = null, at = new Date()): Absorbed {
  const remoteID = epochIDOf(remote), localID = epochIDOf(local);
  const record = compactionRecordOf(local), remoteRecord = compactionRecordOf(remote);
  const remoteHas = (h: string) => A.getBackend(remote).getChangeMetaByHash(h) !== null;
  const absorbed = record ? decodeOrEmpty(record.absorbedHeads).filter(remoteHas) : [];
  let base: RootTree, theirs: RootTree;
  let updated: A.Doc<unknown> | null = null;
  const from = record && sameEpoch(record.from, remoteID) ? decodeHeadsIn(record.fromHeads, remote) : null;
  const to = !from && remoteID.epoch === localID.epoch && remoteRecord ? decodeHeadsIn(remoteRecord.toHeads, remote) : null;
  if (from) {
    // The epoch this one was compacted from: its edits since.
    base = readTree(remote, [...from, ...absorbed]);
    theirs = readTree(remote);
  } else if (to) {
    // Compacted from the same epoch on another device too: its edits since its compaction.
    base = readTree(remote, [...to, ...absorbed]);
    theirs = readTree(remote);
  } else if (archived && sameEpoch(epochIDOf(archived), remoteID)) {
    base = readTree(archived);
    updated = A.merge(A.load(A.save(archived)), remote);
    theirs = readTree(updated);
  } else {
    throw new DifferentEpoch(`Edits made to an earlier version (${describeEpoch(remoteID)}) of this document can't be merged here: `
      + 'it isn’t the version this one was compacted from, and no copy of it is kept here.');
  }
  const ours = readTree(local);
  const merged = withEpochKeysOf(mergeTrees(withoutEpochKeys(base), withoutEpochKeys(ours), withoutEpochKeys(theirs)), ours);
  const doc = commitInto(local, ABSORB_MESSAGE, at, (raw) => {
    if (!sameTree(merged, ours)) writeTree(merged, raw);
    if (!updated && record && !sameTree(theirs, base)) {
      // Remember what's been taken in, so a second delivery adds nothing.
      const known = decodeOrEmpty(record.absorbedHeads).filter((h) => !remoteHas(h));
      const absorbedHeads = encodeHeads([...A.getHeads(remote), ...known]);
      if (absorbedHeads !== compactionRecordOf(raw)?.absorbedHeads) writeCompactionRecord(raw, { ...record, absorbedHeads });
    }
  });
  return { doc, changed: doc !== local, archived: updated };
}

/**
 * `absorbOlderFragmentEpoch` / `absorbOlderProjectEpoch` after the merge: comments re-anchored from `remote` (a fragment)
 * or the checkpoints' project heads remapped (the project), as the Swift does. The fragment's checkpoints in the project
 * document are the caller's (`remapCheckpoints(project, id, remote, doc, [heads])`).
 */
export function absorbOlderEpoch(local: A.Doc<unknown>, remote: A.Doc<unknown>, archived: A.Doc<unknown> | null, o: { fragment?: boolean; project?: boolean; at?: Date } = {}): Absorbed {
  const r = absorbOlder(local, remote, archived, o.at);
  if (!r.changed) return r;
  let doc = r.doc;
  if (o.fragment) doc = reanchor(doc, remote, o.at);
  if (o.project) doc = remapCheckpointProjectHeads(doc, remote, [[...A.getHeads(doc)]], 'Remapped checkpoints after merging an earlier version', o.at);
  return { ...r, doc };
}

export type AdoptionOutcome = 'adopted' | 'rebased' | 'archivedLocalEdits';

export interface Rebase {
  doc: A.Doc<unknown>;
  outcome: AdoptionOutcome;
  /** This device's change carrying the rebased edits, if one was needed. */
  change: Uint8Array | null;
}

/**
 * `DocumentStore.rebase`: merges `local`'s edits the relay hasn't seen into `next`, as a change by `actor`. The base is
 * the synced state (and, on the web, what was rebased already), joined with the state `next` was compacted from when
 * `local` has it; for two compactions of one epoch with nothing synced known, the state `local` compacted from (certain
 * only if both compacted the very same state); without a base the new epoch wins (`archivedLocalEdits`).
 */
export function rebaseOnto(local: EpochSide, next: A.Doc<unknown>, actor: string, at = new Date()): Rebase {
  const ours = readTree(local.doc), theirs = readTree(next);
  const plainOurs = withoutEpochKeys(ours), plainTheirs = withoutEpochKeys(theirs);
  if (sameTree(plainOurs, plainTheirs)) return { doc: next, outcome: 'adopted', change: null };
  const localID = epochIDOf(local.doc), remoteRecord = compactionRecordOf(next);
  const clock = new Set<string>();
  for (const heads of [local.synced, local.rebased ?? []]) if (heads.length && holds(local.doc, heads)) for (const h of heads) clock.add(h);
  if (remoteRecord && sameEpoch(remoteRecord.from, localID)) for (const h of decodeHeadsIn(remoteRecord.fromHeads, local.doc) ?? []) clock.add(h);
  let base: RootTree | null = null;
  let certain = true;
  if (clock.size) base = readTree(local.doc, [...clock]);
  else {
    const own = compactionRecordOf(local.doc);
    const to = remoteRecord && own && sameEpoch(own.from, remoteRecord.from) ? decodeHeadsIn(own.toHeads, local.doc) : null;
    if (to) {
      base = readTree(local.doc, to);
      // Unless both compacted the very same state, the other side may lack some of it.
      certain = own!.fromHeads === remoteRecord!.fromHeads;
    }
  }
  // Without a base there's no telling this device's edits from the old epoch's: the new epoch wins.
  if (!base) return { doc: next, outcome: 'archivedLocalEdits', change: null };
  const plainBase = withoutEpochKeys(base);
  if (sameTree(plainOurs, plainBase)) return { doc: next, outcome: 'adopted', change: null };
  const merged = withEpochKeysOf(mergeTrees(plainBase, plainOurs, plainTheirs), theirs);
  if (sameTree(merged, theirs)) return { doc: next, outcome: certain ? 'adopted' : 'archivedLocalEdits', change: null };
  const raw = A.getBackend(next).fork(actor);
  writeTree(merged, raw);
  raw.commit(REBASE_MESSAGE, seconds(at));
  const change = raw.getLastLocalChange();
  if (!change) return { doc: next, outcome: certain ? 'adopted' : 'archivedLocalEdits', change: null };
  const [doc] = A.applyChanges(next, [change]);
  return { doc, outcome: certain ? 'rebased' : 'archivedLocalEdits', change };
}

export interface Adoption {
  doc: A.Doc<unknown>;
  outcome: AdoptionOutcome;
  /** This device's changes to `next`: the absorption of its synced old-epoch edits, the rebase, the re-anchoring. */
  own: Uint8Array[];
  /** The old side as it now stands: everything in it has been carried over (synced or rebased). */
  archive: EpochSide;
}

/**
 * Adopts `next` (consumed: use the result's `doc`), a newer epoch, in place of `local`: the relay's copy of the old
 * history (`local` at its synced heads) absorbed as the Swift absorbs an older epoch — nothing when the compaction had it
 * all — then this device's unsynced edits rebased (`rebase`), then, for a fragment, comments anchored in the old history
 * re-anchored by their quotes, or for the project, its checkpoints' heads remapped (`adoptProjectEpoch`).
 */
export function adoptEpoch(local: EpochSide, next: A.Doc<unknown>, o: { fragment?: boolean; project?: boolean; at?: Date; actor?: string } = {}): Adoption {
  const actor = o.actor ?? A.getActorId(local.doc);
  const received = [...A.getHeads(next)];
  let doc = next;
  if (local.synced.length && holds(local.doc, local.synced)) {
    try {
      doc = absorbOlder(doc, copyAt(local.doc, local.synced), null, o.at).doc;
    } catch (e) {
      if (!(e instanceof DifferentEpoch)) throw e; // not related through the records: the rebase alone decides
    }
  }
  const r = rebaseOnto(local, doc, actor, o.at);
  doc = r.doc;
  if (o.fragment && doc !== next) doc = reanchor(doc, local.doc, o.at);
  if (o.project) doc = remapCheckpointProjectHeads(doc, local.doc, [received, [...A.getHeads(doc)]], undefined, o.at);
  const own = doc === next ? [] : A.getBackend(doc).getChanges(received);
  return { doc, outcome: r.outcome, own, archive: { doc: local.doc, synced: [...local.synced], rebased: headsList(A.getHeads(local.doc)) } };
}
